import { DecoratorType, JSONObject } from '../../../types/types';
import { resolveStringPlaceholders } from '../../../utils/json/resolveStringPlaceholders';

type ResolvableFormItem = {
  items?: ResolvableFormItem[];
  control?: {
    decorator?: DecoratorType;
  };
  [key: string]: unknown;
};

type ResolvableFormNode = ResolvableFormItem | ResolvableFormItem[];

const resolveDecoratorValue = async (
  formItem: ResolvableFormItem,
  resolveContext: JSONObject
): Promise<ResolvableFormItem> => {
  const decorator = formItem.control?.decorator;

  if (typeof decorator !== 'string') {
    return formItem;
  }

  return {
    ...formItem,
    control: {
      ...formItem.control,
      decorator: await resolveStringPlaceholders(decorator, resolveContext),
    },
  };
};

const resolveFormItemNode = async (
  formNode: ResolvableFormNode,
  resolveContext: JSONObject
): Promise<ResolvableFormNode> => {
  if (Array.isArray(formNode)) {
    return await Promise.all(
      formNode.map(async (nestedNode) => {
        return (await resolveFormItemNode(
          nestedNode,
          resolveContext
        )) as ResolvableFormItem;
      })
    );
  }

  const resolvedFormItem = await resolveDecoratorValue(formNode, resolveContext);

  if (!Array.isArray(formNode.items)) {
    return resolvedFormItem;
  }

  return {
    ...resolvedFormItem,
    items: (await Promise.all(
      formNode.items.map(async (nestedItem) => {
        return (await resolveFormItemNode(
          nestedItem,
          resolveContext
        )) as ResolvableFormItem;
      })
    )) as ResolvableFormItem[],
  };
};

const resolveNestedFormItemDecorators = async (
  formItems: ResolvableFormNode[],
  resolveContext: JSONObject
): Promise<ResolvableFormNode[]> => {
  return await Promise.all(
    formItems.map(async (formNode) => {
      return await resolveFormItemNode(formNode, resolveContext);
    })
  );
};

// resolveContext: full { type, mode, form } payload — mirrors visible/editable evaluator input.
export const resolveFormItemDecorators = async (
  formItems: ResolvableFormNode[],
  resolveContext: JSONObject
) => {
  return await resolveNestedFormItemDecorators(formItems, resolveContext);
};
